import {FC, PropsWithChildren, memo, useCallback, useContext, useState} from 'react';
import {useRecoilState, useRecoilValue, useSetRecoilState} from 'recoil';
import {toast} from 'react-toastify';

import {Box, Button, Stack} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import {ConfirmAction} from '../../../modals/ConfirmAction';
import {ModalContainer} from '../../../modals/ModalContainer';

import {EventBusContext} from '../../../../providers/EventBus';

import {green} from '@mui/material/colors';

import {
  assignToState,
  assignableServiceRequestsState,
  serviceRequestsToAssignQueryTrigger,
} from '../../../../store/service-requests/atoms';

import {ServiceRequestService} from '../../../../api/services/serviceRequest.service';
import {serviceRequestIdsToAssign} from '../../../../store/service-requests/selectors';

const ASSIGN_TO_FN = {
  USER: ServiceRequestService.setWorkers,
  GROUP: ServiceRequestService.setWorkerGroupByIDs,
} as const;

export const AssignToContainerBase: FC<PropsWithChildren> = ({children}) => {
  const triggerRequestsQuery = useSetRecoilState(serviceRequestsToAssignQueryTrigger);
  const setAssignableServiceRequests = useSetRecoilState(assignableServiceRequestsState);
  const [assignTo, setAssignTo] = useRecoilState(assignToState);
  const assignableServiceRequestIds = useRecoilValue(serviceRequestIdsToAssign);
  const {bus} = useContext(EventBusContext);

  const [open, setOpen] = useState(false);

  const toggleModal = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const cancelAssignTo = useCallback(() => {
    setAssignTo([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assignServiceRequests = async (setLoading: (arg: boolean) => void) => {
    setLoading(true);

    const assignToUserIds = (assignTo || []).reduce<(number | string)[]>((acc, item) => {
      if (item.type === 'USER') {
        return [...acc, item.id];
      }
      return acc;
    }, []);

    try {
      await ServiceRequestService.setWorkers(assignableServiceRequestIds, assignToUserIds);

      // TODO использовать когда будут группы использоваться
      // await ServiceRequestService.setWorkerGroupByIDs(assignableServiceRequestIds, assignTo.id);

      setAssignableServiceRequests({
        0: [],
      });
      setAssignTo([]);
      toast.success('Успешно назначено', {containerId: 'default'});
      toggleModal();
      triggerRequestsQuery((v) => v + 1);
    } catch (err) {
      bus?.emit('error', [err]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{pt: 4, paddingX: 2, pb: 1, height: 'calc(100% - 48px)'}}>
      <Stack direction="column" sx={{height: '100%'}}>
        {children}
        <Stack direction="row" sx={{justifyContent: 'space-between', mt: 2}}>
          <Button variant="contained" color="error" size="small" disabled={!assignTo} onClick={cancelAssignTo}>
            Отмена
          </Button>
          <LoadingButton
            sx={{
              backgroundColor: green[500],
              '&:not(.Mui-disabled):hover': {
                backgroundColor: green[600],
              },
              color: '#fff',
            }}
            variant="contained"
            color="success"
            size="small"
            onClick={toggleModal}
            disabled={!assignTo || !assignableServiceRequestIds.length}
          >
            Назначить
          </LoadingButton>
        </Stack>
      </Stack>
      {open && (
        <ModalContainer
          sideEffect={assignServiceRequests}
          renderModal={(sideEffect, loading) => (
            <ConfirmAction
              loading={loading}
              open={open}
              handleClose={toggleModal}
              handleSideEffect={sideEffect}
              title="Подтвердите назначение"
              buttonTitle="Назначить"
            />
          )}
        />
      )}
    </Box>
  );
};

export const AssignToContainer = memo(AssignToContainerBase);
